import { PRISM_SCRIPT_URL, getView } from '../../engine/prism.js';
import { readProjectFile } from '../../engine/host.js';
import { ApiError, api } from '../../lib/api.js';
import {
  charToByte,
  normalizeCompletions,
  normalizeDiagnostics,
  parsePreviewResult,
  type Completion,
  type PreviewRequest,
  type PreviewResult,
  type ProcgenDiagnostic,
} from './procgen-doc.js';

/**
 * Where procedural previews, checks and completions run (generators.md
 * sections 10.3 and 12.1), in this order:
 *
 * 1. prism_wasm's standalone `ProcgenPreview` class in a Web Worker
 *    (procgen-preview.worker.ts). Feature-detected: older bundles lack it.
 *    Files the graph needs come back as `missing`; they are read from the
 *    project (the editor overlay first), added, and the call is repeated
 *    (at most {@link MAX_FILE_ROUNDS} rounds).
 * 2. `PrismView.procgenPreview(json)` / `procgenCheck(json)` on the live
 *    Scene view (it preloads its own files and reuses the host's caches).
 * 3. The server: `POST /api/projects/:id/procgen/preview { request }` and
 *    `POST /api/projects/:id/procgen/check { roots }` (the CLI; it reads the
 *    saved files, so unsaved edits are flushed first).
 *
 * Everything takes its backends as arguments ({@link ProcgenBackends}) so
 * the order and the file loop are testable without a DOM or wasm.
 */

/** The wasm `ProcgenPreview` API (section 8.3.15); results may be async (worker). */
export interface ProcgenEngine {
  addFile(path: string, bytes: Uint8Array): void | Promise<void>;
  removeFile?(path: string): void | Promise<void>;
  preview(json: string): string | Promise<string>;
  check?(json: string): string | Promise<string>;
  scan?(json: string): string | Promise<string>;
  complete?(json: string): string | Promise<string>;
  hover?(json: string): string | Promise<string>;
  /** Canonical printer (section 5.13); absent in bundles that do not export it. */
  format?(json: string): string | Promise<string>;
  schema?(): string | Promise<string>;
  clear?(): void | Promise<void>;
}

/** `PrismView.procgenPreview` / `procgenCheck`: JSON in, JSON out. */
export type ViewCall = (json: string) => Promise<string>;

export interface ProcgenBackends {
  /** The worker class, or null when this engine build has none. */
  engine: () => Promise<ProcgenEngine | null>;
  /** The live scene view's `procgenPreview`, or null. */
  view: () => ViewCall | null;
  /** The live scene view's `procgenCheck`, or null. */
  viewCheck?: () => ViewCall | null;
  /** Server preview (`{ request }`), or null when there is no project. */
  server: ((request: PreviewRequest) => Promise<unknown>) | null;
  /** Server check (`{ roots }`). */
  serverCheck?: ((roots: string[]) => Promise<unknown>) | null;
  /** Reads a project file (the in-editor overlay wins). */
  readFile: (path: string) => Promise<Uint8Array>;
  /** Runs before a server call (saves the open document). */
  beforeServer?: () => Promise<void>;
}

export type PreviewSource = 'worker' | 'view' | 'server';

export interface PreviewOutcome {
  result: PreviewResult;
  source: PreviewSource;
  warnings: string[];
  /** Missing-file rounds the worker needed. */
  rounds: number;
}

export interface CheckOutcome {
  diagnostics: ProcgenDiagnostic[];
  source: PreviewSource;
  warnings: string[];
}

/** Rounds of "read the missing files and call again". */
export const MAX_FILE_ROUNDS = 4;

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

function normalizeFile(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\.?\/+/, '');
}

function missingOf(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];
  const m = (value as { missing?: unknown }).missing;
  return Array.isArray(m) ? m.filter((p): p is string => typeof p === 'string' && p.length > 0).map(normalizeFile) : [];
}

/**
 * Calls `run` and, while the answer lists `missing` files, reads them,
 * adds them to the engine and calls again (at most `maxRounds` rounds).
 * Files that cannot be read are not retried; the last answer reports them.
 */
export async function withMissingFiles<T>(
  engine: Pick<ProcgenEngine, 'addFile'>,
  run: () => Promise<T>,
  readFile: (path: string) => Promise<Uint8Array>,
  maxRounds = MAX_FILE_ROUNDS,
): Promise<{ value: T; rounds: number; added: string[]; unreadable: string[] }> {
  const tried = new Set<string>();
  const added: string[] = [];
  const unreadable: string[] = [];
  let rounds = 0;
  for (;;) {
    const value = await run();
    const missing = missingOf(value).filter((p) => !tried.has(p));
    if (missing.length === 0 || rounds >= maxRounds) return { value, rounds, added, unreadable };
    rounds++;
    let any = false;
    for (const path of missing) {
      tried.add(path);
      try {
        const bytes = await readFile(path);
        await engine.addFile(path, bytes);
        added.push(path);
        any = true;
      } catch {
        unreadable.push(path);
      }
    }
    if (!any) return { value, rounds, added, unreadable };
  }
}

/** One preview call against the engine class, with the missing-file loop. */
export async function previewWithFiles(
  engine: ProcgenEngine,
  request: PreviewRequest,
  readFile: (path: string) => Promise<Uint8Array>,
  maxRounds = MAX_FILE_ROUNDS,
): Promise<{ result: PreviewResult; rounds: number; added: string[]; unreadable: string[] }> {
  const json = JSON.stringify(request);
  const out = await withMissingFiles(engine, async () => parsePreviewResult(await engine.preview(json)), readFile, maxRounds);
  return { result: out.value, rounds: out.rounds, added: out.added, unreadable: out.unreadable };
}

/** Preview with the fallback order worker class → scene view → server. */
export async function runPreview(request: PreviewRequest, backends: ProcgenBackends): Promise<PreviewOutcome> {
  const warnings: string[] = [];
  let engine: ProcgenEngine | null = null;
  try {
    engine = await backends.engine();
  } catch (error) {
    warnings.push(`browser procgen engine unavailable: ${errorText(error)}`);
  }
  if (engine) {
    try {
      // `host` only means something to the live scene view.
      const { host: _host, ...plain } = request;
      const out = await previewWithFiles(engine, plain, backends.readFile);
      for (const p of out.unreadable) warnings.push(`could not read ${p}`);
      return { result: out.result, source: 'worker', warnings, rounds: out.rounds };
    } catch (error) {
      warnings.push(`browser preview failed (${errorText(error)}); trying the next backend`);
    }
  }
  const view = backends.view();
  if (view) {
    try {
      return { result: parsePreviewResult(await view(JSON.stringify(request))), source: 'view', warnings, rounds: 0 };
    } catch (error) {
      warnings.push(`scene view preview failed (${errorText(error)}); trying the server`);
    }
  }
  if (backends.server) {
    await backends.beforeServer?.();
    const { host: _host, ...plain } = request;
    return { result: parsePreviewResult(await backends.server(plain)), source: 'server', warnings, rounds: 0 };
  }
  throw new Error(['No procedural preview backend is available (open a project, or run pnpm engine:build).', ...warnings].join(' '));
}

/** `check` with the same order; `roots` are the generator paths. */
export async function runCheck(roots: string[], backends: ProcgenBackends): Promise<CheckOutcome> {
  const warnings: string[] = [];
  const json = JSON.stringify({ roots, graph: roots });
  let engine: ProcgenEngine | null = null;
  try {
    engine = await backends.engine();
  } catch (error) {
    warnings.push(`browser procgen engine unavailable: ${errorText(error)}`);
  }
  if (engine?.check) {
    const check = engine.check.bind(engine);
    try {
      // Preload what the graph reads (scan lists it), then check.
      if (engine.scan) {
        const scan = engine.scan.bind(engine);
        await withMissingFiles(engine, async () => parseJson(await scan(json)), backends.readFile);
      }
      const raw = parseJson(await check(json));
      return { diagnostics: normalizeDiagnostics(raw), source: 'worker', warnings };
    } catch (error) {
      warnings.push(`browser check failed (${errorText(error)})`);
    }
  }
  const view = backends.viewCheck?.() ?? null;
  if (view) {
    try {
      return { diagnostics: normalizeDiagnostics(parseJson(await view(json))), source: 'view', warnings };
    } catch (error) {
      warnings.push(`scene view check failed (${errorText(error)})`);
    }
  }
  if (backends.serverCheck) {
    await backends.beforeServer?.();
    return { diagnostics: normalizeDiagnostics(await backends.serverCheck(roots)), source: 'server', warnings };
  }
  throw new Error(['No procedural check backend is available.', ...warnings].join(' '));
}

function parseJson(text: unknown): unknown {
  if (typeof text !== 'string') return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** CompleteRequest (section 8.3.12): `offset` is a UTF-8 byte offset into `text`. */
export interface CompleteRequest {
  file: string;
  text: string;
  path: string;
  offset: number;
  graph: string[];
}

export function completeRequest(file: string, path: string, text: string, caret: number, graph: string[]): CompleteRequest {
  return { file, text, path, offset: charToByte(text, caret), graph };
}

/** Engine completions; null when the engine (or its `complete`) is unavailable. */
export async function engineComplete(engine: ProcgenEngine | null, req: CompleteRequest): Promise<Completion[] | null> {
  if (!engine?.complete) return null;
  try {
    return normalizeCompletions(await engine.complete(JSON.stringify(req)));
  } catch {
    return null;
  }
}

/** Engine hover text (markdown-ish); null when unavailable or empty. */
export async function engineHover(engine: ProcgenEngine | null, req: CompleteRequest): Promise<string | null> {
  if (!engine?.hover) return null;
  try {
    const raw = await engine.hover(JSON.stringify(req));
    const parsed = parseJson(raw);
    if (typeof parsed === 'string') return parsed || null;
    if (parsed && typeof parsed === 'object') {
      const text = (parsed as { text?: unknown; hover?: unknown }).text ?? (parsed as { hover?: unknown }).hover;
      return typeof text === 'string' && text ? text : null;
    }
    return typeof raw === 'string' && raw && raw !== 'null' ? raw : null;
  } catch {
    return null;
  }
}

/** Canonical text of an expression; the input when the engine has no printer. */
export async function engineFormat(engine: ProcgenEngine | null, text: string): Promise<string> {
  if (!engine?.format) return text;
  try {
    const raw = await engine.format(JSON.stringify({ text }));
    const parsed = parseJson(raw);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object' && typeof (parsed as { text?: unknown }).text === 'string') return (parsed as { text: string }).text;
    return text;
  } catch {
    return text;
  }
}

/* ------------------------------------------------------------------ */
/* Image                                                               */

/** Base64 → bytes (no Buffer: works in browsers, workers and Node 16+). */
export function decodeBase64(text: string): Uint8Array {
  const clean = text.replace(/^data:[^,]*,/, '').replace(/\s+/g, '');
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Writes one response's pixels into the full-size RGBA buffer. A chunk that
 * covers the whole image replaces it; otherwise it is placed at `rows[0]`.
 * Returns the buffer (a new one when the size changed).
 */
export function mergeRgba(buffer: Uint8ClampedArray | null, size: [number, number], result: PreviewResult): Uint8ClampedArray {
  const [w, h] = size;
  const out = buffer && buffer.length === w * h * 4 ? buffer : new Uint8ClampedArray(w * h * 4);
  if (typeof result.rgba !== 'string' || !result.rgba) return out;
  const bytes = decodeBase64(result.rgba);
  if (bytes.length >= w * h * 4) {
    out.set(bytes.subarray(0, w * h * 4));
    return out;
  }
  const row = Array.isArray(result.rows) ? Math.max(0, Math.min(h, result.rows[0])) : 0;
  const start = row * w * 4;
  out.set(bytes.subarray(0, Math.max(0, Math.min(bytes.length, out.length - start))), start);
  return out;
}

/** A continuation chunk merged into the earlier result (overlays deduplicated by ref + id). */
export function mergeResult(prev: PreviewResult | null, next: PreviewResult): PreviewResult {
  if (!prev || prev.size?.[0] !== next.size?.[0] || prev.size?.[1] !== next.size?.[1]) return next;
  const merge = <T extends { ref: string; id: string }>(a: T[] | undefined, b: T[] | undefined): T[] => {
    const seen = new Set((a ?? []).map((i) => `${i.ref}#${i.id}`));
    return [...(a ?? []), ...(b ?? []).filter((i) => !seen.has(`${i.ref}#${i.id}`))];
  };
  return {
    ...prev,
    ...next,
    points: merge(prev.points, next.points),
    areas: merge(prev.areas, next.areas),
    paths: merge(prev.paths, next.paths),
    legend: next.legend?.length ? next.legend : prev.legend,
    probe: next.probe ?? prev.probe,
  };
}

/* ------------------------------------------------------------------ */
/* Browser backends                                                    */

type WorkerMethod = 'preview' | 'check' | 'scan' | 'complete' | 'hover' | 'format' | 'schema' | 'clear';

interface WorkerReply {
  type: 'ready' | 'result' | 'error';
  id?: number;
  ok?: boolean;
  reason?: string | null;
  methods?: string[];
  text?: string;
  message?: string;
}

let worker: Worker | null = null;
let workerEngine: Promise<ProcgenEngine | null> | null = null;
/** Why the worker class is unavailable (shown in the panel). */
let workerReason: string | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (text: string) => void; reject: (error: Error) => void }>();
/** path → fingerprint of the bytes the worker holds. */
const sentFiles = new Map<string, string>();

function fingerprint(bytes: Uint8Array): string {
  let h = 2166136261;
  for (let i = 0; i < bytes.length; i++) h = Math.imul(h ^ bytes[i]!, 16777619);
  return `${bytes.length}:${h >>> 0}`;
}

function call(method: WorkerMethod, json?: string): Promise<string> {
  const w = worker;
  if (!w) return Promise.reject(new Error('procgen worker not running'));
  const id = nextId++;
  return new Promise<string>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ type: 'call', id, method, json });
  });
}

function postFile(path: string, bytes: Uint8Array): void {
  const key = normalizeFile(path);
  const fp = fingerprint(bytes);
  if (!worker || sentFiles.get(key) === fp) return;
  sentFiles.set(key, fp);
  const copy = bytes.slice();
  worker.postMessage({ type: 'addFile', path: key, bytes: copy }, [copy.buffer]);
}

function makeEngine(methods: readonly string[]): ProcgenEngine {
  const has = (m: WorkerMethod) => methods.includes(m);
  const engine: ProcgenEngine = {
    addFile: (path, bytes) => postFile(path, bytes),
    removeFile: (path) => {
      const key = normalizeFile(path);
      sentFiles.delete(key);
      worker?.postMessage({ type: 'removeFile', path: key });
    },
    preview: (json) => call('preview', json),
  };
  if (has('check')) engine.check = (json) => call('check', json);
  if (has('scan')) engine.scan = (json) => call('scan', json);
  if (has('complete')) engine.complete = (json) => call('complete', json);
  if (has('hover')) engine.hover = (json) => call('hover', json);
  if (has('format')) engine.format = (json) => call('format', json);
  if (has('schema')) engine.schema = () => call('schema');
  if (has('clear')) engine.clear = async () => void (await call('clear'));
  return engine;
}

/** The worker-backed `ProcgenPreview`, started once; null when unavailable. */
export function procgenWorkerEngine(): Promise<ProcgenEngine | null> {
  if (workerEngine) return workerEngine;
  workerEngine = new Promise<ProcgenEngine | null>((resolve) => {
    if (typeof Worker === 'undefined') {
      workerReason = 'Web Workers are not available here';
      resolve(null);
      return;
    }
    try {
      worker = new Worker(new URL('./procgen-preview.worker.ts', import.meta.url), { type: 'module' });
    } catch (error) {
      workerReason = `could not start the procgen worker: ${errorText(error)}`;
      resolve(null);
      return;
    }
    const w = worker;
    w.onmessage = (event: MessageEvent<WorkerReply>) => {
      const msg = event.data;
      if (msg.type === 'ready') {
        if (msg.ok) {
          workerReason = null;
          resolve(makeEngine(msg.methods ?? ['preview']));
        } else {
          workerReason = msg.reason ?? 'procgen engine unavailable';
          resolve(null);
        }
        return;
      }
      const entry = msg.id === undefined ? undefined : pending.get(msg.id);
      if (!entry) return;
      pending.delete(msg.id!);
      if (msg.type === 'result') entry.resolve(msg.text ?? '');
      else entry.reject(new Error(msg.message ?? 'procgen call failed'));
    };
    w.onerror = (event) => {
      workerReason = `procgen worker failed: ${event.message || 'unknown error'}`;
      resolve(null);
      for (const [, p] of pending) p.reject(new Error('procgen worker crashed'));
      pending.clear();
    };
    w.postMessage({ type: 'init', url: new URL(PRISM_SCRIPT_URL, location.href).href });
  });
  return workerEngine;
}

/** Why the browser class is not used (null = in use or not tried yet). */
export function procgenWorkerReason(): string | null {
  return workerReason;
}

/** Forget a failed load (after `pnpm engine:build`) so the next call retries. */
export function resetProcgenWorker(): void {
  worker?.terminate();
  worker = null;
  workerEngine = null;
  workerReason = null;
  sentFiles.clear();
  for (const [, p] of pending) p.reject(new Error('procgen worker restarted'));
  pending.clear();
}

/** A project file changed: the worker drops it and reads it again when needed. */
export function invalidateProcgenFile(path: string): void {
  const key = normalizeFile(path);
  if (!sentFiles.has(key)) return;
  sentFiles.delete(key);
  worker?.postMessage({ type: 'removeFile', path: key });
}

/** Sends the editor's current text of a file to the worker (no-op until it runs). */
export function syncProcgenFile(path: string, text: string): void {
  if (!worker) return;
  postFile(path, new TextEncoder().encode(text));
}

/** The scene view's method by name, if this engine build has it (feature-detected). */
function viewMethod(name: 'procgenPreview' | 'procgenCheck' | 'procgenBake'): ViewCall | null {
  const view = getView('scene');
  if (!view?.hasMethod(name)) return null;
  return async (json) => String(await view.callMethod<string>(name, [json]));
}

export const viewProcgenPreview = (): ViewCall | null => viewMethod('procgenPreview');
export const viewProcgenCheck = (): ViewCall | null => viewMethod('procgenCheck');
export const viewProcgenBake = (): ViewCall | null => viewMethod('procgenBake');

/** A server reply that still carries a report (errors found) counts as an answer. */
async function postReport(path: string, body: unknown): Promise<unknown> {
  try {
    return await api.post<unknown>(path, body);
  } catch (error) {
    if (error instanceof ApiError && error.body && typeof error.body === 'object') {
      const b = error.body as Record<string, unknown>;
      if (Array.isArray(b.diagnostics) || typeof b.ok === 'boolean') return b;
      throw new Error(typeof b.message === 'string' ? b.message : error.message);
    }
    throw error;
  }
}

/** The real backends for a project. */
export function browserBackends(projectId: number | null, beforeServer?: () => Promise<void>): ProcgenBackends {
  return {
    engine: procgenWorkerEngine,
    view: viewProcgenPreview,
    viewCheck: viewProcgenCheck,
    server: projectId ? (request) => postReport(`/projects/${projectId}/procgen/preview`, { request }) : null,
    serverCheck: projectId ? (roots) => postReport(`/projects/${projectId}/procgen/check`, { roots }) : null,
    readFile: (path) => (projectId ? readProjectFile(projectId, path) : Promise.reject(new Error('no project open'))),
    ...(beforeServer ? { beforeServer } : {}),
  };
}
